import React from 'react'
import styled from 'styled-components'
import type { PDFDisclaimerProps } from '../ExportPDFModal/types'

const DEFAULT_ACCENT_COLOR = '#0294df'
const DEFAULT_TEXT_COLOR = '#1e293b'
const DEFAULT_SUBTEXT_COLOR = '#64748b'

const DEFAULT_DISCLAIMER_TEXT = `This document is provided for informational purposes only. The data and information contained herein have been compiled from sources believed to be reliable. However, no warranty or representation, expressed or implied, is made as to the accuracy, completeness, or reliability of the information. Any reliance you place on such information is strictly at your own risk. The content is subject to change without notice.`

const PageWrapper = styled.div.attrs({
  className: 'print-last-page',
})`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 26px;
  box-sizing: border-box;
`

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 32px;
`

const LogoContainer = styled.div`
  margin-bottom: 20px;
`

const LogoImage = styled.img`
  max-width: 160px;
  max-height: 64px;
  object-fit: contain;
`

const LogoFallback = styled.div<{ $accentColor: string }>`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    ${({ $accentColor }) => $accentColor} 0%,
    ${({ $accentColor }) => `${$accentColor}dd`} 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
`

const LogoText = styled.span`
  font-size: 26px;
  font-weight: 700;
  color: white;
`

const DisclaimerSection = styled.div.attrs({
  className: 'print-disclaimer',
})`
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
`

const DisclaimerTitle = styled.h4<{ $textColor: string }>`
  margin: 0 0 10px 0;
  font-size: 10px;
  font-weight: 600;
  color: ${({ $textColor }) => $textColor};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const DisclaimerText = styled.p<{ $subtextColor: string }>`
  margin: 0 0 12px 0;
  font-size: 8px;
  line-height: 1.6;
  color: ${({ $subtextColor }) => $subtextColor};
  text-align: justify;
`

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
  margin-top: auto;
`

const FooterCompany = styled.span<{ $subtextColor: string }>`
  font-size: 10px;
  color: ${({ $subtextColor }) => $subtextColor};
`

const FooterPage = styled.span<{ $subtextColor: string }>`
  font-size: 10px;
  color: ${({ $subtextColor }) => $subtextColor};
`

const GeneratedDate = styled.div<{ $subtextColor: string }>`
  margin-top: 12px;
  margin-bottom: 16px;
  font-size: 8px;
  color: ${({ $subtextColor }) => $subtextColor};
  text-align: center;
`

/**
 * Disclaimer/last page component for PDF exports
 * Displays company branding, disclaimer text, and page number
 */
export const PDFDisclaimer: React.FC<PDFDisclaimerProps> = ({
  pageNumber,
  totalPages,
  company,
  disclaimerText = DEFAULT_DISCLAIMER_TEXT,
  accentColor = DEFAULT_ACCENT_COLOR,
  textColor = DEFAULT_TEXT_COLOR,
  subtextColor = DEFAULT_SUBTEXT_COLOR,
}) => {
  const companyInitial = company?.name?.charAt(0).toUpperCase() || 'P'
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <PageWrapper>
      <ContentArea>
        <LogoContainer>
          {company?.logo ? (
            <LogoImage src={company.logo} alt={company.name || 'Company logo'} />
          ) : (
            <LogoFallback $accentColor={accentColor}>
              <LogoText>{companyInitial}</LogoText>
            </LogoFallback>
          )}
        </LogoContainer>
      </ContentArea>

      <DisclaimerSection>
        <DisclaimerTitle $textColor={textColor}>Disclaimer</DisclaimerTitle>
        <DisclaimerText $subtextColor={subtextColor}>{disclaimerText}</DisclaimerText>

        <GeneratedDate $subtextColor={subtextColor}>
          Document generated with Popety.io on {today}
        </GeneratedDate>
      </DisclaimerSection>

      <Footer>
        <FooterCompany $subtextColor={subtextColor}>
          {company?.name || 'Popety.io'} - {company?.address || ''}
        </FooterCompany>
        <FooterPage $subtextColor={subtextColor}>
          Page {pageNumber} of {totalPages}
        </FooterPage>
      </Footer>
    </PageWrapper>
  )
}
